import type { FC, PropsWithChildren } from 'react'
import styled from '@emotion/styled'

import sanitizeHtml from 'sanitize-html'

import Typography from '@segment/matcha/components/Typography'

import CopyTextBase, * as CopyText from '@segment/matcha/recipes/CopyText'
import SectionBase, * as Section from '@segment/matcha/recipes/Section'

import { SectionWithAccordion } from 'views/Infrastructure/accordionData'

import getAbsoluteUrl from '@segment/utils/getAbsoluteUrl'

import type { PageConfig } from '@segment/contexts/page'
import { usePageConfig } from '@segment/contexts/page'

import AccordionSection from '../AccordionSection'

import ProcessingDeduplicationImage from './ProcessingDeduplicationImage'

const getData = (pageConfig: PageConfig) => ({
  anchorId: `deduplication`,
  overline: `Processing`,
  heading: `Deduplication`,
  description: `What’s worse than missing data? Duplicated data. It’s impossible to trust your analysis if events show up 2, 3, or more times. That’s why we’ve invested heavily in our <a href="${getAbsoluteUrl(
    `https://segment.com/blog/exactly-once-delivery/`,
    pageConfig
  )}">Deduplication Infrastructure</a>. It reduces duplicate data by 0.6% over a 30d window.`,
  link1: `https://news.ycombinator.com/item?id=14664405`,
  link1Text: `Delivering billions of messages exactly once (133 Comments)`,
  link2: `https://github.com/segmentio/kafka-go`,
  link2Text: `segmentio/kafka-go (2,500 stars)`,
  accordionSection: SectionWithAccordion.DEDUPLICATION,
})

const ProcessingDeduplication: FC<PropsWithChildren<unknown>> = () => {
  const pageConfig = usePageConfig()
  const data = getData(pageConfig)
  const { anchorId, overline, heading, description, link1, link1Text, link2, link2Text, accordionSection } = data
  return (
    <Wrapper id={anchorId}>
      <SectionBase variant="2Col" copyVariant="wide">
        <Section.Copy>
          <CopyTextBase>
            <CopyText.Overline>{overline}</CopyText.Overline>
            <CopyText.Heading>{heading}</CopyText.Heading>
            <CopyText.Description tag="div">
              <CopyTextDescriptionSection
                dangerouslySetInnerHTML={{
                  __html: sanitizeHtml(description, { allowedTags: [`a`] }),
                }}
              />
              <CopyTextDescriptionSection>
                <Typography variant="pLarge">
                  <a href={link1}>{link1Text}</a>
                </Typography>
              </CopyTextDescriptionSection>
              <CopyTextDescriptionSection>
                <Typography variant="pLarge">
                  <a href={link2}>{link2Text}</a>
                </Typography>
              </CopyTextDescriptionSection>
              <AccordionSection section={accordionSection} />
            </CopyText.Description>
          </CopyTextBase>
        </Section.Copy>
        <StyledSectionContents>
          <ProcessingDeduplicationImage />
        </StyledSectionContents>
      </SectionBase>
    </Wrapper>
  )
}

const CopyTextDescriptionSection = styled.div`
  margin-bottom: 32px;
`

const Wrapper = styled.section`
  display: grid;
`

const StyledSectionContents = styled(Section.Contents)`
  margin: auto;
`

export default ProcessingDeduplication
